type ProductValue = string | number | null | undefined;
type PoolProduct = Partial<
  Record<
    | 'variety'
    | 'labeled_variety'
    | 'caliber'
    | 'color'
    | 'category'
    | 'box_format'
    | 'format'
    | 'complete_format'
    | 'box_label'
    | 'label'
    | 'unit_weight'
    | 'box_weight'
    | 'fruit_segregation',
    ProductValue
  >
>;

const normalizeToken = (value: ProductValue) =>
  (value ?? '').toString().trim().toUpperCase();

const normalizeNumberToken = (value: ProductValue) => {
  if (value == null || String(value).trim() === '') return '';
  const numericValue = Number(value);
  return Number.isFinite(numericValue)
    ? numericValue.toString()
    : normalizeToken(value);
};

// Legacy weight-only selections now select the full format as well.
// Normalize on read so existing saved settings work without a database migration.
export const normalizePresettlementProductDefinition = (
  value: unknown
): string[] => {
  const fields = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split('-')
      : [];
  const normalized = fields
    .filter(
      (field): field is string => typeof field === 'string' && !!field.trim()
    )
    .map((field) =>
      field.trim() === 'unit_weight' ? 'box_format' : field.trim()
    );
  return normalized.length ? [...new Set(normalized)] : ['variety', 'caliber'];
};

const buildCompleteFormat = (box: PoolProduct) => {
  // Prefer source fields; accept complete_format when older data lacks them.
  const completeFormat = normalizeToken(box.complete_format);
  const parts = completeFormat.match(/^(.*)-(\d+(?:\.\d+)?)\s*KG$/);
  const packaging =
    normalizeToken(box.box_format ?? box.format) ||
    parts?.[1] ||
    completeFormat;
  const weight = normalizeNumberToken(
    box.unit_weight ?? box.box_weight ?? parts?.[2]
  );
  return `${packaging}-${weight}KG`;
};

// Used by pricing, the reconciliation breakdown, and the detail export.
export const buildPoolProductKey = (box: PoolProduct, fields: string[]) => {
  const safeFields = normalizePresettlementProductDefinition(fields);
  return safeFields
    .map((field) => {
      if (field === 'variety') {
        return normalizeToken(box.labeled_variety ?? box.variety);
      }
      if (field === 'box_format') {
        return buildCompleteFormat(box);
      }
      if (field === 'box_label') {
        return normalizeToken(box.box_label ?? box.label);
      }
      return normalizeToken(box[field as keyof PoolProduct]);
    })
    .join('|');
};
